import styled from '@emotion/styled';
import classNames from 'classnames';
import { FC, KeyboardEvent, MouseEvent, PropsWithChildren, useEffect, useMemo, useRef } from 'react';

import { useIsOnScreen } from 'app/hooks';

import { useDidMount } from '../../hooks';
import Box from '../Box';

// Local Typings
export interface MenuItemProps {
  isDisabled?: boolean;
  isFocused?: boolean;
  isSelected: boolean;
  onClick?: (evt: MouseEvent<HTMLLIElement>) => void;
  onKeyDown?: (evt: KeyboardEvent<HTMLLIElement>) => void;
  smallPadding?: boolean;
  tabIndex?: number;
  testId?: string;
  withBorderBottom?: boolean;
}
interface StyledBoxProps {
  withBorderBottom: boolean;
}

// Local Variables
const StyledBox = styled(Box)<StyledBoxProps>(({ theme, withBorderBottom }) => ({
  borderBottom: withBorderBottom ? `2px solid ${theme.semanticColors.menu_item_border_bottom}` : undefined,
  color: theme.semanticColors.menu_item_text,
  transition: 'background-color 250ms',

  /* eslint-disable @typescript-eslint/naming-convention,sort-keys */
  '&:has(li:hover)': {
    backgroundColor: theme.semanticColors.menu_item_li_hover_background,
  },

  '&:focus, &:hover': {
    backgroundColor: theme.semanticColors.menu_item_focus_background,
  },

  '&.disabled': {
    backgroundColor: theme.semanticColors.menu_item_disabled_background,
    color: theme.semanticColors.menu_item_disabled_text,
  },

  '&.focused': {
    backgroundColor: theme.semanticColors.menu_item_focus_background,
  },

  '&.selected': {
    backgroundColor: theme.semanticColors.menu_item_selected_background,

    '&:focus, &:hover': {
      backgroundColor: theme.semanticColors.menu_item_selected_focus_background,
    },
  },

  '&.selected.focused': {
    backgroundColor: theme.semanticColors.menu_item_selected_focus_background,
  },
  /* eslint-enable @typescript-eslint/naming-convention,sort-keys */
}));

// Component Definition
const MenuItem: FC<PropsWithChildren<MenuItemProps>> = ({
  testId,
  ...props
}) => {
  const ref = useRef<HTMLLIElement>(null);

  const didMount = useDidMount();

  const isOnScreen = useIsOnScreen(ref);

  useEffect(() => {
    if (didMount && props.isSelected && !isOnScreen) {
      ref.current?.scrollIntoView();
    }
  },
  /*
      Do not pass isOnScreen into the dep array
      If we do, we will experience strange scroll behavior,
      as documented in this story: https://triumphbcap.visualstudio.com/TPay%20Projects/_workitems/edit/44306
      We only want "scrollIntoView" to run when the selected value changes
      */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [didMount, props.isSelected]);

  const className = useMemo(() => {
    if (props.isFocused && props.isDisabled) {
      return 'focused disabled';
    }

    if (props.isDisabled) {
      return 'disabled';
    }

    if (props.isSelected && props.isFocused) {
      return 'selected focused';
    }

    if (props.isFocused) {
      return 'focused';
    }

    if (props.isSelected) {
      return 'selected';
    }

    return '';
  },
  [props.isDisabled, props.isFocused, props.isSelected]);

  return (
    <StyledBox
      {...props}
      as="li"
      className={classNames(className, 'menu_menu-item')}
      cursor="pointer"
      minWidth="fit-content"
      noWrap
      outline="none"
      paddingLeft={props.smallPadding ? 2 : 3}
      paddingRight={props.smallPadding ? undefined : 12}
      paddingY={props.smallPadding ? 2 : 1}
      ref={ref}
      testId={testId}
      withBorderBottom={!!props.withBorderBottom}
    />
  );
};

export default MenuItem;
